import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import FloorItem from './FloorItem/FloorItem'
import BuildingItem from './BuildingItem/BuildingItem'
import {_L, useLocaleState} from '../../context/LocaleContext'
import ActionButton from '../ActionButton/ActionButton'

export const DIRECTION = {
  VERTICAL: 'column',
  HORIZONTAL: 'row'
}

const Container = styled.div`
  display: flex;
  flex-direction: ${props => props.direction};
  border-top: 1px solid #eff0f3;
  box-sizing: border-box;
  height: ${props =>
  props.direction === DIRECTION.HORIZONTAL ? '70px' : 'calc(100vh - 110px)'};
  width: 100%;
`

const ListGroup = styled.ul`
  list-style-type: none;
  display: flex;
`

const StyledActionButton = styled(ActionButton)`
box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.05);
margin: 7px 10px 7px 10px;

&:focus{
  box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.05);
  outline: 3px solid #FF9900;
  outline-offset: 4px
}

  @media (max-width: 420px) {
	  height: 50px;
	  width: 50px;
  }
`

const mergeItems = (itemsPerFloor, floorList, availableOnly) =>
  floorList.reduce((acc, curVal, index) => {
    let count = itemsPerFloor[curVal] ? itemsPerFloor[curVal].avail_space : 0

    // Include only floors with available rooms when availableOnly is true
    if (availableOnly) {
      if (count) {
        acc[index] = {flId: curVal, count}
      }
    } else {
      acc[index] = {flId: curVal, count}
    }

    return acc
  }, {})

const FloorList = ({
  buildingCode,
  onSelected,
  floorList,
  direction,
  itemsPerFloor,
  availableOnly,
  onBuildingClick,
  showBuilding,
  selectedFloor,
  showHome,
  onHomeClick,
}) => {
  const localeState = useLocaleState()

  const onFloorSelect = floorId =>
    onSelected({bl_id: buildingCode, fl_id: floorId})
    

  const label = _L({key: 'Floor', localeState})
  const floors = mergeItems(itemsPerFloor, floorList, availableOnly)

  let {flId: selectedFlId} = selectedFloor
  let currentKey = ""
	  
  const onFloorKeyDown = evt => {
      if(evt.key == "Enter" || evt.key == " "){
    	currentKey = evt.target.dataset.key
        onSelected({bl_id: buildingCode, fl_id: currentKey})
      }
    }

  return (
    <Container direction={direction}>
      {showBuilding && <BuildingItem onClick={onBuildingClick} />}
      {showHome && (
    	        <StyledActionButton
    	          onClick={onHomeClick}
    	          icon="HOME"
    	          backgroundColor="#ffffff"
    	          data-testid="tab-home-button"
    	          aria-label="Home"
    	        />
    	      )}
      <ListGroup>
      {Object.values(floors).map(item => {
        const {flId, count} = item
        return (
          <FloorItem
            key={flId}
            value={flId}
            label={label}
            selected={selectedFlId === flId}
            count={count}
            dataKey={flId}
            onClick={onFloorSelect}
            onKeyDown={onFloorKeyDown}
            direction={direction}
          />
        )
      })}
      </ListGroup>
    </Container>
  )
}

FloorList.propTypes = {
  /**
   * List of floors to display in the list. Array of floor codes
   */
  floorList: PropTypes.array.isRequired,

  /**
   * The building code of the displayed floors
   */
  buildingCode: PropTypes.string.isRequired,

  /**
   * Object containing the mapping of the number of available items per floor, i.e {01:3,02:10}
   */
  itemsPerFloor: PropTypes.object.isRequired,

  /**
   * The direction of the list. Can be HORIZONTAL or VERTICAL
   */
  direction: PropTypes.string.isRequired,

  /**
   * Executed when a floor item is selected
   */
  onSelected: PropTypes.func,

  /**
   * Displays only floors that have available space when true.
   */
  availableOnly: PropTypes.bool.isRequired,

  selectedFloor: PropTypes.object.isRequired,

  onBuildingClick: PropTypes.func,

  showBuilding: PropTypes.bool.isRequired
}

FloorList.defaultProps = {
  floorList: [],
  itemsPerFloor: {},
  direction: DIRECTION.HORIZONTAL,
  availableOnly: false,
  showBuilding: false
}

export default FloorList
